import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
// Components
import Box from '../../shared/atoms/Box';
import Heading from '../../shared/atoms/Heading';
import Text from '../../shared/atoms/Text';
import Link from '../../shared/atoms/Link';
import Button from '../../shared/atoms/Button';
import Separator from '../../Separator';
import BikeDetailsDisplay from './BikeDetailsDisplay';
import AppointmentDetails from './AppointmentDetails';
import CourseChat from './CourseChat';
import CourseBill from './CourseBill';
import CounterProposalsList from './CounterProposalsList';
import CurrentAppointmentBlock from './CurrentAppointmentBlock';
import FutureQuoteBlock from './FutureQuoteBlock';
import BikeDiagnosticAccordion from './BikeDiagnosticAccordion';
import FinishedAppointmentCard from './FinishedAppointmentCard';
import CourseSectionHeading from './CourseSectionHeading';
import BrandIcon from './BrandIcon';
import PromoBanner from '../../shared/molecules/PromoBanner';
import AppPromoCard from '../../shared/organisms/AppPromoCard';
import { formatDate } from '../../../utils/reparation';
import { RJ } from '../../rapport/rapportTokens';
import AngleDown16 from '../../icons/AngleDown16';

const StyledBox = styled(Box)`
  border: 1px solid ${(p) => p.theme.colors.gray.light};
`;

// Chevron rotates 180° when the <details> is open — pure CSS, SSR-safe.
const SummaryChevron = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  color: ${RJ.textSecondary};
  transition: transform 0.2s ease;
  flex-shrink: 0;
`;

// §5.5 — "Votre demande initiale" : the original auto-diagnostic, demoted and
// collapsed by default at the bottom of the finished hub. Native <details> so
// it stays SSR-safe (no client state, no hydration mismatch).
const InitialRequest = styled.details`
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
  background: ${RJ.white};
  margin-top: 24px;

  & > summary {
    list-style: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
  }

  & > summary::-webkit-details-marker {
    display: none;
  }

  &[open] > summary ${SummaryChevron} {
    transform: rotate(180deg);
  }
`;

const PhotosContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 0.4rem;
  .displayed-photo {
    height: 6rem;
    position: relative;
    img {
      object-fit: cover;
      height: 100%;
      width: 100%;
      overflow: hidden;
      border-radius: 4px;
    }
  }
`;

const SaveCardButton = styled(Button)`
  background: #d5ff4f;
  color: #2e414b;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: #c5ef3f;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CourseDetails = ({
  course,
  client,
  relatedPhotos,
  openModal,
  counterProposals = [],
  hasCounterProposals = false,
  onCounterProposalAccepted,
  onCounterProposalsRefresh,
  onSelectProposal,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const photos = Array.isArray(relatedPhotos) ? relatedPhotos : [];
  const setModification =
    course.state !== 'finished' && course.state !== 'cancelled';
  // Afficher le bloc uniquement si la course est en attente (pas encore scheduled/finished/cancelled)
  const showSaveCardBanner =
    course.state === 'pending' ||
    course.state === 'dispatched' ||
    course.state === 'scheduled';

  // §5 — post-intervention hub: a dedicated, ordered layout for the finished
  // state (Cette intervention → Le rapport → Votre rendez-vous → Demande
  // initiale repliée). All other states keep the legacy layout below, untouched.
  if (course.state === 'finished') {
    return (
      <Box>
        {course.prix > 0 && course.company_id == 0 ? (
          <CourseBill course={course} />
        ) : (
          <CurrentAppointmentBlock course={course} />
        )}

        <Box mb={9}>
          <CourseSectionHeading icon={<BrandIcon name="rapport" size={28} />}>
            {t('course.report.heading')}
          </CourseSectionHeading>
          <BikeDiagnosticAccordion report={course.intervention_report} />
          <FutureQuoteBlock course={course} />
        </Box>

        <FinishedAppointmentCard course={course} />

        <Box mb={6}>
          <AppPromoCard placement="confirm" />
        </Box>

        <InitialRequest data-testid="course-initial-request">
          <summary>
            <BrandIcon name="demande-initiale" size={24} />
            <Box>
              <Text fontWeight="bold" mb={0} color={RJ.text}>
                {t('course.initialrequest.heading')}
              </Text>
              <Text color="gray.darker" fontSize="sm" mb={0}>
                {t('course.initialrequest.subtitle', {
                  date: formatDate(course.date, 'DD/MM/YYYY'),
                })}
              </Text>
            </Box>
            <SummaryChevron aria-hidden="true">
              <AngleDown16 />
            </SummaryChevron>
          </summary>
          <Box px={{ xs: 4, lg: 5 }} pb={{ xs: 4, lg: 5 }}>
            <Box mb={1}>
              <Text fontWeight="bold">{t('selfdiagnosis')}</Text>
            </Box>
            <BikeDetailsDisplay bikeDetails={course.bike_details} />
            <Separator axe="horizontal" bg="gray.light" my={4} />
            <Box mb={1}>
              <Text fontWeight="bold">{t('details')}</Text>
            </Box>
            <Box>
              {course.course_details ? (
                <Text>{course.course_details}</Text>
              ) : (
                <Text color="gray.dark">{t('coursedetails.texts.1')}</Text>
              )}
            </Box>
            <Separator axe="horizontal" bg="gray.light" my={4} />
            <Box mb={1}>
              <Text fontWeight="bold">{t('pictures')}</Text>
            </Box>
            <Box>
              {photos.length ? (
                <PhotosContainer>
                  {photos.map(({ small }, index) => (
                    <div className="displayed-photo" key={index}>
                      <img src={small} />
                    </div>
                  ))}
                </PhotosContainer>
              ) : (
                <Text color="gray.dark">{t('coursedetails.texts.3')}</Text>
              )}
            </Box>
          </Box>
        </InitialRequest>

        {/* Chat history with the fixer — demoted below the initial request:
            its header repeats the appointment infos already shown by
            FinishedAppointmentCard (§5.4), so it stays out of the main flow. */}
        <Box mt={6}>
          <CourseChat
            client={client}
            course={course}
            showAppointmentRecap={false}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* DEVIS-QA-13 — section order: current RDV, then the intervention
          details (bill), then the full diagnostic, then the next-RDV
          recommendations, then appointment details and self-diag/photos. */}
      <CurrentAppointmentBlock course={course} />
      <BikeDiagnosticAccordion report={course.intervention_report} />
      <FutureQuoteBlock course={course} />
      {course.state === 'scheduled' || course.state === 'finished' ? (
        <CourseChat client={client} course={course} />
      ) : (
        <AppointmentDetails
          course={course}
          openModal={openModal}
          setModification={setModification}
        />
      )}
      {course.state === 'dispatched' && hasCounterProposals && (
        <Box mb={5}>
          <CounterProposalsList
            courseId={course.id}
            proposals={counterProposals}
            onAccepted={onCounterProposalAccepted}
            onRefresh={onCounterProposalsRefresh}
            onSelectProposal={onSelectProposal}
            zoneId={course.zone_id}
          />
        </Box>
      )}

      {showSaveCardBanner && (
        <Box mb={6}>
          <AppPromoCard placement="confirm" />
        </Box>
      )}

      {/* Save Card Banner */}
      {showSaveCardBanner && (
        <Box mb={6}>
          <PromoBanner
            icon="💳"
            title="Simplifiez vos prochains paiements"
            description="Pour régler en un clic après votre réparation, enregistrez dès maintenant votre carte bancaire dans votre espace client. Ainsi, plus besoin de ressaisir vos coordonnées : le paiement sera ultra-rapide une fois l'intervention validée.">
            <SaveCardButton onClick={() => history.push('/account/cartes')}>
              J&apos;enregistre ma carte bancaire
            </SaveCardButton>
          </PromoBanner>
        </Box>
      )}

      <Box>
        <Heading level={4} mb={4}>
          {t('coursedetails.heading')}
        </Heading>
        <StyledBox borderRadius="xs" p={4} mb={3}>
          <Box justifyContent="space-between">
            <Box flexDirection="row" justifyContent="space-between" mb={1}>
              <Text fontWeight="bold">{t('selfdiagnosis')}</Text>
            </Box>
            <BikeDetailsDisplay bikeDetails={course.bike_details} />
          </Box>
          <Separator axe="horizontal" bg="gray.light" my={4} />
          <Box justifyContent="space-between">
            <Box flexDirection="row" justifyContent="space-between" mb={1}>
              <Text fontWeight="bold">{t('details')}</Text>
              {setModification && (
                <Link
                  color="blue.electric"
                  onClick={() => openModal('UPDATE_DETAILS')}>
                  {t('modify.cta')}
                </Link>
              )}
            </Box>
            <Box>
              {course.course_details ? (
                <Text>{course.course_details}</Text>
              ) : course.state === 'finished' ||
                course.state === 'cancelled' ? (
                <Text color="gray.dark">{t('coursedetails.texts.1')}</Text>
              ) : (
                <Text color="gray.dark">{t('coursedetails.texts.2')}</Text>
              )}
            </Box>
          </Box>
          <Separator axe="horizontal" bg="gray.light" my={4} />
          <Box justifyContent="space-between">
            <Box flexDirection="row" justifyContent="space-between" mb={1}>
              <Text fontWeight="bold">{t('pictures')}</Text>
              {setModification && (
                <Link
                  color="blue.electric"
                  onClick={() => openModal('UPDATE_PHOTOS')}>
                  {t('modify.cta')}
                </Link>
              )}
            </Box>
            <Box>
              {photos.length ? (
                <PhotosContainer>
                  {photos.map(({ small }, index) => (
                    <div className="displayed-photo" key={index}>
                      <img src={small} />
                    </div>
                  ))}
                </PhotosContainer>
              ) : course.state === 'finished' ||
                course.state === 'cancelled' ? (
                <Text color="gray.dark">{t('coursedetails.texts.3')}</Text>
              ) : (
                <Text color="gray.dark">{t('coursedetails.texts.4')}</Text>
              )}
            </Box>
          </Box>
        </StyledBox>
      </Box>
    </Box>
  );
};

export default CourseDetails;
